import type { CreateFusionConfig } from '@wf-coe/types';
import {
  Button,
  HelperText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
} from 'flowbite-react';
import { type FormEvent, useEffect, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import { SecretsRowButton } from '../../components/clients/SecretsRowButton.js';
import {
  useCheckFusionCredentialsMutation,
  type FusionCredentialsCheck,
} from '../../queries/use-check-fusion-credentials.js';

/**
 * Fields the operator can manage on the per-fusion-config Secrets
 * bundle. Mirrors `SECRET_FIELDS['fusion-config']` in
 * `apps/admin-console-api/src/lib/secret-bundle.ts`.
 *
 * Everything tied to the customer's Adobe + Fusion identity lives
 * here — both the Fusion API auth (`clientId`, `clientSecret`, ADR 0026) and
 * the Adobe SSO login creds that screenshot-capture uses for the
 * browser-driven flow at app.workfrontfusion.com. The Adobe IMS
 * triplet moved here from the per-license bundle on 2026-05-13 (same
 * rationale as `fusionImsToken` the day before — one Adobe identity
 * per customer's Fusion tenant, shared across sister licenses).
 */
const FUSION_CONFIG_BUNDLE_FIELDS = [
  {
    key: 'clientId',
    label: 'Fusion API client ID',
    hint: 'Adobe Developer Console → Workfront Fusion API → OAuth Server-to-Server credential.',
    kind: 'string' as const,
  },
  {
    key: 'clientSecret',
    label: 'Fusion API client secret',
    hint: 'The same OAuth Server-to-Server credential.',
    kind: 'string' as const,
  },
  {
    key: 'adobeImsEmail',
    label: 'Adobe IMS email',
    hint: 'Adobe SSO account used by screenshot-capture at app.workfrontfusion.com.',
    kind: 'string' as const,
  },
  { key: 'adobeImsPassword', label: 'Adobe IMS password', kind: 'string' as const },
  {
    key: 'adobeImsTwoFactorBypass',
    label: 'Adobe IMS 2FA bypass',
    hint: 'Optional. Required for IMS accounts with TOTP enabled.',
    kind: 'string' as const,
  },
];
import { useParams } from 'react-router-dom';
import { useCreateFusionConfigMutation } from '../../queries/use-create-fusion-config.js';
import { useDeleteFusionConfigMutation } from '../../queries/use-delete-fusion-config.js';
import { type FusionConfigDto, useFusionConfigsQuery } from '../../queries/use-fusion-configs.js';
import { useUpdateFusionConfigMutation } from '../../queries/use-update-fusion-config.js';

/**
 * `/clients/{id}/fusion-connections` — manages a tenant's Workfront
 * Fusion connections. Lighter than Workfront Connections (no OAuth
 * pickers): just name + url.
 */
export function FusionConnections(): JSX.Element {
  const { id: clientId } = useParams<{ id: string }>();
  const { data, error, isLoading, isError } = useFusionConfigsQuery(clientId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<FusionConfigDto | null>(null);
  const [deleting, setDeleting] = useState<FusionConfigDto | null>(null);

  const handleNew = (): void => {
    setEditing(null);
    setEditorOpen(true);
  };
  const handleEdit = (config: FusionConfigDto): void => {
    setEditing(config);
    setEditorOpen(true);
  };

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
          Fusion Connections
        </h2>
        <Button size="sm" onClick={handleNew} disabled={!clientId}>
          <FiPlus className="mr-1" /> New Fusion connection
        </Button>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
          Loading Fusion connections…
        </p>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error instanceof Error ? error.message : 'Failed to load Fusion connections'}
        </div>
      ) : data && data.length === 0 ? (
        <EmptyState onCreate={handleNew} />
      ) : data ? (
        <ConfigsTable
          configs={data}
          clientId={clientId}
          onEdit={handleEdit}
          onDelete={setDeleting}
        />
      ) : null}

      {clientId ? (
        <ConfigEditModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          clientId={clientId}
          existing={editing}
        />
      ) : null}
      {clientId && deleting ? (
        <ConfigDeleteModal
          open={Boolean(deleting)}
          onClose={() => setDeleting(null)}
          clientId={clientId}
          config={deleting}
        />
      ) : null}
    </section>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center dark:border-gray-600 dark:bg-gray-800">
      <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
        No Fusion connections yet
      </h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
        Add a Workfront Fusion API URL so audit + backup scenarios know where to call.
      </p>
      <Button size="sm" className="mx-auto mt-4" onClick={onCreate}>
        <FiPlus className="mr-1" /> New Fusion connection
      </Button>
    </div>
  );
}

function ConfigsTable({
  configs,
  clientId,
  onEdit,
  onDelete,
}: {
  configs: FusionConfigDto[];
  clientId: string | undefined;
  onEdit: (config: FusionConfigDto) => void;
  onDelete: (config: FusionConfigDto) => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell>URL</TableHeadCell>
            <TableHeadCell>Fusion API</TableHeadCell>
            <TableHeadCell>
              <span className="sr-only">Actions</span>
            </TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
          {configs.map((config) => (
            <TableRow key={config.id} className="bg-white dark:bg-gray-800">
              <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                {config.name}
              </TableCell>
              <TableCell className="font-mono text-xs">{config.url}</TableCell>
              <TableCell>
                <CredentialsCheckCell clientId={clientId} fusionConfigId={config.id} />
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  {clientId ? (
                    <SecretsRowButton
                      bundlePath={`/v1/clients/${clientId}/fusion-configs/${config.id}`}
                      fields={FUSION_CONFIG_BUNDLE_FIELDS}
                      title={`${config.name} — secrets`}
                      ariaLabel={`Manage secrets for ${config.name}`}
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onEdit(config)}
                    aria-label={`Edit ${config.name}`}
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(config)}
                    aria-label={`Delete ${config.name}`}
                    className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Check credentials (ADR 0026): on click, mints a token from the Fusion
 * config's S2S credential and shows the region and Fusion org it reached.
 */
function CredentialsCheckCell({
  clientId,
  fusionConfigId,
}: {
  clientId: string | undefined;
  fusionConfigId: string;
}): JSX.Element {
  const check = useCheckFusionCredentialsMutation(clientId, fusionConfigId);
  return (
    <div className="flex items-center gap-2">
      <Button
        size="xs"
        color="light"
        disabled={!clientId || check.isPending}
        onClick={() => check.mutate()}
      >
        {check.isPending ? 'Checking…' : 'Check credentials'}
      </Button>
      {check.isError ? <CheckPill tone="red" text="Check failed" /> : null}
      {check.data ? <CredentialsCheckView result={check.data} /> : null}
    </div>
  );
}

export function CredentialsCheckView({ result }: { result: FusionCredentialsCheck }): JSX.Element {
  if (!result.ok) {
    if (result.reason === 'org-not-reachable') {
      return <CheckPill tone="red" text={`Org ${result.organizationId} not reachable`} />;
    }
    const text =
      result.reason === 'credentials-missing'
        ? 'Not set'
        : result.reason === 'ims-rejected'
          ? `Adobe IMS rejected (${result.status ?? '?'})`
          : `Fusion rejected (${result.status ?? '?'})`;
    return (
      <CheckPill tone={result.reason === 'credentials-missing' ? 'gray' : 'red'} text={text} />
    );
  }
  if (result.orgMismatch) {
    return (
      <CheckPill
        tone="amber"
        text={`Org ${result.organizationId} (${result.region}) — licenses use ${result.licenseOrgIds.join(', ')}`}
      />
    );
  }
  if (result.missingTeamIds.length > 0) {
    return (
      <CheckPill tone="amber" text={`Teams not visible: ${result.missingTeamIds.join(', ')}`} />
    );
  }
  return <CheckPill tone="green" text={`OK — org ${result.organizationId}, ${result.region}`} />;
}

function CheckPill({
  tone,
  text,
}: {
  tone: 'green' | 'amber' | 'red' | 'gray';
  text: string;
}): JSX.Element {
  const classes = {
    green: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  }[tone];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${classes}`}>
      {text}
    </span>
  );
}

function ConfigEditModal({
  open,
  onClose,
  clientId,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  existing: FusionConfigDto | null;
}): JSX.Element {
  const create = useCreateFusionConfigMutation();
  const update = useUpdateFusionConfigMutation();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setUrl(existing?.url ?? '');
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  const isPending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const canSubmit = name.trim() !== '' && url.trim() !== '' && !isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const body: CreateFusionConfig = { name: name.trim(), url: url.trim() };
    if (existing) {
      update.mutate({ clientId, configId: existing.id, patch: body }, { onSuccess: onClose });
    } else {
      create.mutate({ clientId, body }, { onSuccess: onClose });
    }
  };

  return (
    <Modal show={open} onClose={onClose} dismissible size="lg">
      <ModalHeader>{existing ? 'Edit Fusion connection' : 'New Fusion connection'}</ModalHeader>
      <form onSubmit={handleSubmit} aria-label="Fusion connection form">
        <ModalBody>
          <div className="space-y-4">
            <div>
              <Label htmlFor="fusion-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="fusion-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isPending}
              />
            </div>
            <div>
              <Label htmlFor="fusion-url">
                URL <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="fusion-url"
                type="url"
                required
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://eu1.make.com/api/v2"
                disabled={isPending}
              />
              <HelperText className="mt-1">Workfront Fusion (Make.com) API base URL.</HelperText>
            </div>
            {error ? (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
              >
                {error instanceof Error ? error.message : 'Save failed'}
              </div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isPending ? 'Saving…' : existing ? 'Save' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function ConfigDeleteModal({
  open,
  onClose,
  clientId,
  config,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  config: FusionConfigDto;
}): JSX.Element {
  const del = useDeleteFusionConfigMutation();

  const handleConfirm = (): void => {
    del.mutate({ clientId, configId: config.id }, { onSuccess: onClose });
  };

  return (
    <Modal show={open} onClose={onClose} dismissible size="md">
      <ModalHeader>Delete Fusion connection</ModalHeader>
      <ModalBody>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          This will permanently delete <span className="font-semibold">{config.name}</span>. Any
          licenses pointing at it lose their reference (set to null) — the licenses themselves stay.
        </p>
        {del.isError ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {del.error instanceof Error ? del.error.message : 'Failed to delete'}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose} disabled={del.isPending}>
          Cancel
        </Button>
        <Button color="failure" onClick={handleConfirm} disabled={del.isPending}>
          {del.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
